import { useCallback, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { useDescribeStringGroupQuery } from 'src/generated/graphql';
import {
  NewStringGroupMetadata,
  StringGroup,
  TranslationStatus,
} from '../types';

/**
 * Client id assigned to a group prefilled from the Create String Group modal.
 * A group authored in this UI is by definition LangHub-owned, so the prefilled
 * page must be editable (isLangHubClient → canEdit true).
 */
const NEW_STRING_GROUP_CLIENT_ID = 'langhub';

/**
 * Maps the form data handed over by the Create String Group modal to the
 * StringGroup shape the detail page components expect. Fields the form does
 * not know (version, counts, actors, translation state) get new-group
 * defaults: version 1, zero strings, empty progress, status Complete. These
 * defaults are local placeholders — nothing has been persisted server-side
 * yet (the create mutation is still a TODO in NamespaceDetailPage).
 */
export function mapNewStringGroupMetadata(
  newGroupMetadata: NewStringGroupMetadata,
): StringGroup {
  return {
    stringGroupId: newGroupMetadata.stringGroupId,
    namespaceId: newGroupMetadata.namespaceId,
    clientId: NEW_STRING_GROUP_CLIENT_ID,
    description: newGroupMetadata.description || undefined,
    teamName: newGroupMetadata.teamName || undefined,
    createdOn: newGroupMetadata.createdOn,
    createdBy: newGroupMetadata.createdBy,
    version: 1,
    cxFamilyId: newGroupMetadata.cxFamilyId,
    cxIds: newGroupMetadata.cxIds,
    sourceLocale: newGroupMetadata.sourceLocale,
    stringCount: 0,
    // A freshly created group has never been submitted for translation, so it
    // has no "last translation submitted" moment yet. Left at 0/'' (rendered as
    // "—" by MetadataPanel) rather than defaulted to the creation time, which
    // would falsely claim a submission the user has not made.
    updatedOn: 0,
    updatedBy: '',
    translationStatus: TranslationStatus.Complete,
    translationProgress: {
      completedLocales: [],
      inProgressLocales: [],
      failedLocales: [],
      cancelledLocales: [],
    },
  };
}

interface UseStringGroupDataResult {
  data: StringGroup | null;
  loading: boolean;
  error: Error | null;
  /** Raw Apollo error preserved for extension inspection (e.g. errorType). */
  apolloError: ApolloError | undefined;
  /**
   * True while the page is rendering create-modal prefill metadata and the
   * server query is still skipped (no server-side group exists yet). Flips to
   * false the moment the prefill is dismissed (first refetch), which is the
   * signal dependent data (e.g. the strings table's ListStrings query) must
   * gate its own skip on — a static `!!newGroupMetadata` would stay skipped
   * for the life of the mount and never load once real data exists.
   */
  isPrefill: boolean;
  /**
   * Refetches the group and resolves with the observed version number (or
   * `undefined` when the refetch fails / returns no data — e.g. a transient
   * StringGroupNotReadyException while TIS is still ingesting a fresh submit).
   * Callers that must sequence work after fresh server data lands (e.g. polling
   * until a newly-published version is queryable before clearing a draft) can
   * await it and compare the returned version.
   */
  refetch: () => Promise<number | undefined>;
}

/**
 * Fetches string group metadata (identity, version, translation status and per-locale progress)
 * via the describeStringGroup query, a DynamoDB-only metadata read. The source-string snapshot is
 * fetched separately by `useStringsData` (paginated ListStrings, per version), so this hook no
 * longer returns strings.
 *
 * When `newGroupMetadata` is provided (the page was reached from the Create String Group
 * modal), the backend is NOT queried: the metadata is rendered straight from
 * the form data, because the group has not been created server-side at all —
 * the create mutation is not wired yet (TODO in NamespaceDetailPage), so
 * describeStringGroup has nothing to return. The first `refetch()` call (fired
 * after submit/cancel/rollback) permanently dismisses the prefill and switches
 * to server data — un-skipping the query makes Apollo run it automatically.
 */
export function useStringGroupData(
  namespaceId: string,
  stringGroupId: string,
  newGroupMetadata?: NewStringGroupMetadata | null,
): UseStringGroupDataResult {
  const [prefillDismissed, setPrefillDismissed] = useState(false);
  const prefillActive = !!newGroupMetadata && !prefillDismissed;

  const { data, loading, error, refetch } = useDescribeStringGroupQuery({
    variables: { namespaceId, stringGroupId },
    fetchPolicy: 'cache-and-network',
    skip: prefillActive,
  });

  // Memoized so the returned refetch keeps a stable identity across renders.
  // Consumers use it as a useEffect dependency (e.g. the submission-poll
  // interval); a fresh function each render would tear down and recreate that
  // interval on every re-render, resetting the poll countdown so it may never
  // fire. Apollo's own `refetch` is stable, so these only change if it does.
  const serverRefetch = useCallback(
    (): Promise<number | undefined> => observedVersion(refetch),
    [refetch],
  );
  // Dismissing the prefill un-skips the query for subsequent renders; Apollo
  // also executes a refetch() issued against a skipped query, so this first
  // call already returns a real observed version — letting
  // waitForPublishedVersion's polling work from a prefilled page too. A failed
  // fetch resolves to undefined ("not available yet").
  const prefillRefetch = useCallback(async (): Promise<number | undefined> => {
    setPrefillDismissed(true);
    return observedVersion(refetch);
  }, [refetch]);

  if (prefillActive) {
    return {
      data: mapNewStringGroupMetadata(newGroupMetadata),
      loading: false,
      error: null,
      apolloError: undefined,
      isPrefill: true,
      refetch: prefillRefetch,
    };
  }

  const group = data?.describeStringGroup;

  const mapped: StringGroup | null = group
    ? {
        stringGroupId: group.stringGroupId,
        namespaceId: group.namespaceId,
        clientId: group.clientId,
        description: group.description ?? undefined,
        teamName: group.teamName ?? undefined,
        createdOn: group.createdOn ?? 0,
        createdBy: group.createdBy ?? '',
        version: group.version,
        cxFamilyId: group.cxFamilyId,
        cxIds: group.cxIds,
        // Fallback must use BCP-47 hyphen form (LocaleIdentifier pattern
        // `^[a-z]{2,3}(-[A-Z0-9]{2,3}){0,1}$`); the underscore form `en_US`
        // is rejected by SubmitStringGroupTranslation validation.
        sourceLocale: group.sourceLocale ?? 'en-US',
        stringCount: group.stringCount,
        updatedOn: group.updatedOn ?? 0,
        updatedBy: group.updatedBy ?? '',
        cancelRequestedOn: group.cancelRequestedOn ?? undefined,
        cancelRequestedBy: group.cancelRequestedBy ?? undefined,
        traceId: group.traceId ?? undefined,
        translationStatus: group.translationStatus,
        translationProgress: {
          completedLocales: group.translationProgress.completedLocales,
          inProgressLocales: group.translationProgress.inProgressLocales,
          failedLocales: group.translationProgress.failedLocales,
          cancelledLocales: group.translationProgress.cancelledLocales,
        },
      }
    : // After the prefill is dismissed the real query fires; keep rendering the
      // prefilled metadata only while that query is still in its INITIAL
      // in-flight state (loading with no response yet) so the page does not
      // blank to a full-page spinner (which would hide the flashbar carrying
      // the success message the dismissing action just queued). Once the query
      // has produced a response, a null group is authoritative (not found /
      // deleted / failed load) and must surface the same empty+error state as
      // the never-prefilled path instead of stale prefill metadata.
      newGroupMetadata && loading && !data
      ? mapNewStringGroupMetadata(newGroupMetadata)
      : null;

  return {
    data: mapped,
    loading,
    error: toError(error),
    apolloError: error,
    isPrefill: false,
    // Resolve with the observed version so callers can poll for a freshly
    // published version. A rejected refetch (e.g. StringGroupNotReadyException
    // while TIS ingests) resolves to `undefined` rather than throwing, so it is
    // treated as "not available yet" and never surfaces as an unhandled
    // rejection; load failures are still reported via the query's own `error`.
    refetch: serverRefetch,
  };
}

/**
 * Runs an Apollo refetch and resolves with the version it observed, or
 * `undefined` when the fetch fails or returns no data — the shared
 * "not available yet" signal both the prefill and server-backed refetch
 * paths report to version-polling callers.
 */
async function observedVersion(
  refetch: ReturnType<typeof useDescribeStringGroupQuery>['refetch'],
): Promise<number | undefined> {
  try {
    const result = await refetch();
    const v = result.data?.describeStringGroup?.version;
    return v != null ? Number(v) : undefined;
  } catch {
    return undefined;
  }
}

function toError(error: ApolloError | undefined): Error | null {
  if (!error) return null;
  return new Error(error.message);
}
